import { useState, useMemo } from 'react';
import Page from '../../../components/PageWrapper';
import moment from 'moment';
import {
  getAllFuelSettlements,
  getUnclosedFuelVendorDailySales,
  getUnsettledFuelBalance,
  requestNonCashSaleSettlement,
  settleStationNonCashSale
} from '../../../api/requests';
import GTable from '../../../components/elements/GTable';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import UnclosedSaleDetails from './UnclosedSaleDetails';
import TabNav, { ITab } from '../../../components/tabs/TabNav';
import TabContent from '../../../components/tabs/TabContent';
import { FuelSalesStatus } from '../../../components/statuses';
import { IoWalletOutline } from 'react-icons/io5';
import useUserAccount from '../../../hooks/userAccountHook';
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { GTableActionColumn, ITableRowAction } from '../../../components/elements/GTable/GTable';
import { showToast } from '../../../utils/miscFunctions';

const FuelSalesAuditview = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useUserAccount();
  const [toggleSaleDetailsView, setToggleSaleDetailsView] = useState<boolean>(false);
  const [completedSales, setCompletedSales] = useState<any>([]);
  const [nonCashSettlements, setNonCashSettlements] = useState<any>([]);
  const [selectedSale, setSelectedSale] = useState<any>(null);
  const [unsettledFuelBalance, setUnsettledFuelBalance] = useState<number>(0);
  const [showPerPage, setShowPerPage] = useState<number>(10);
  const [activeTab, setActiveTab] = useState('PENDING');
  const [activeSettlementTab, setActiveSettlementTab] = useState('settled');
  const queryClient = useQueryClient();

  const getTabs = (): ITab[] => [
    { id: 'PENDING', title: 'Pending' },
    { id: 'CLOSED', title: 'Completed' }
  ];

  const getSettlemtTabs = () => {
    let tabs: ITab[] = [
      {
        id: 'unsettled',
        title: 'Pending'
      },
      {
        id: 'settled',
        title: 'Completed'
      }
    ];

    return tabs;
  };

  const fetchUnclosedSales = async () => {
    setLoading(true);
    try {
      if (user.isFuelAdmin()) {
        const res = await getUnclosedFuelVendorDailySales(activeTab);
        setCompletedSales(res);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettlements = async () => {
    const completed = activeSettlementTab == 'settled' ? true : false;
    try {
      if (user.isFuelManager()) {
        const res = await getAllFuelSettlements(completed);
        setNonCashSettlements(res);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUnsettledFuelBalance = async () => {
    try {
      if (user.isFuelAdmin()) {
        const res = await getUnsettledFuelBalance();
        setUnsettledFuelBalance(res?.amount);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useQuery({
    queryKey: ['fetchUnsettledFuelBalance'],
    queryFn: fetchUnsettledFuelBalance
  });

  useQuery({
    queryKey: ['fetchUnclosedSales', activeTab],
    queryFn: fetchUnclosedSales
  });

  const { isLoading: isSettlementLoading } = useQuery({
    queryKey: ['fetchSettlements', activeSettlementTab],
    queryFn: fetchSettlements
  });

  const paymentSettlementColumns = useMemo(() => {
    const cols: any = [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Station',
            accessor: 'submittedBy.company.name',
            Cell: ({ cell }: any) => <span>{cell.value ?? '-'}</span>
          },
          {
            Header: 'Amount',
            accessor: 'amount',
            Cell: ({ cell }: any) => <span>{cell.value ?? '-'}</span>
          },
          {
            Header: 'Requestor',
            accessor: 'submittedBy',
            Cell: ({ row }: any) => (
              <span>
                {row.original.submittedBy?.firstName} {row.original.submittedBy?.lastName ?? '-'}
              </span>
            )
          },
          {
            Header: 'Requested On',
            accessor: 'submittedOn',
            Cell: ({ cell }: any) => (
              <span>{cell.value ? moment(cell.value).format('Do MMM YY, h:mm a') : '-'}</span>
            )
          },
          {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ cell }: any) => {
              return <FuelSalesStatus row={cell} />;
            }
          },
          {
            Header: 'Settled On',
            accessor: 'receivedOn',
            Cell: ({ cell }: any) => (
              <span>{cell.value ? moment(cell.value).format('MMM Do YY, h:mm a') : '-'}</span>
            )
          }
        ]
      }
    ];

    if (activeSettlementTab === 'unsettled') {
      cols.push({
        id: 'Action',
        Header: 'Actions',
        Cell: (row: any) => {
          if(getPendingMomoActions(row.row.original).length > 0) return (
            <GTableActionColumn actions={getPendingMomoActions(row.row.original)}/>
          );
        }
      });
    }

    return cols;
  }, [activeSettlementTab]);

  const columns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Date',
            accessor: 'salesMadeOn',
            Cell: ({ cell }: any) => (
              <span>{cell.value ? moment(cell.value).format('Do MMM YY') : '-'}</span>
            )
          },
          {
            Header: 'Amount',
            accessor: 'amount',
            Cell: ({ cell }: any) => <span>{cell.value ?? '-'}</span>
          },
          {
            Header: 'Quantity',
            accessor: 'quantity',
            Cell: ({ cell }: any) => <span>{cell.value ?? '-'} L</span>
          },
          {
            Header: 'Vendor',
            accessor: 'submittedBy',
            Cell: ({ row }: any) => (
              <span>
                {row.original.submittedBy?.firstName} {row.original.submittedBy?.lastName ?? '-'}
              </span>
            )
          },
          {
            Header: 'Submitted On',
            accessor: 'submittedOn',
            Cell: ({ cell }: any) => (
              <span>{cell.value ? moment(cell.value).format('Do MMM YY, h:mm') : '-'}</span>
            )
          },
          {
            Header: 'Received On',
            accessor: 'receivedOn',
            Cell: ({ cell }: any) => (
              <span>{cell.value ? moment(cell.value).format('MMM Do YY, h:mm') : '-'}</span>
            )
          },
          {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ cell }: any) => {
              return <FuelSalesStatus row={cell} />;
            }
          }
        ]
      }
    ],
    []
  );

  const transactionsColumns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Date',
            accessor: 'fuelSale.salesMadeOn',
            Cell: ({ cell }: any) => (
              <span>{cell.value ? moment(cell.value).format('Do MMM YY') : '-'}</span>
            )
          },
          {
            Header: 'Amount',
            accessor: 'amount',
            Cell: ({ cell }: any) => <span>{cell.value ?? '-'}</span>
          },
          {
            Header: 'Quantity',
            accessor: 'quantity',
            Cell: ({ cell }: any) => <span>{cell.value ?? '-'}</span>
          },
          {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ cell }: any) => {
              return <FuelSalesStatus row={cell} />;
            }
          }
        ]
      }
    ],
    []
  );

  const getPendingMomoActions = (data: any): ITableRowAction[] => {
    const actions = [];
    actions.push({
      label: 'Approve',
      callback: () => approveMomoSettlement(data?.id)
    });

    return actions;
  };

  const approveMomoSettlement = async (id: string) => {
    try {
      const response = await settleStationNonCashSale(id);
      showToast.success(response?.message ?? 'Settlement approved succesfully');
      queryClient.invalidateQueries({ queryKey: ['fetchSettlements'] });
    } catch (error: any) {
      showToast.error(error?.description ?? 'Failed to approve settlement');
    }
  };

  const adminRequestNonCashSettlement = async () => {
    try {
      const response = await requestNonCashSaleSettlement();
      showToast.success(response?.message ?? 'Settlement requested succesfully');
    } catch (error: any) {
      showToast.error(error?.description ?? 'Failed to request settlement');
    }
  };

  return (
    <Page title="Fuel Sales Audit">
      <div className="bg-white w-full min-h-full p-5 h-fit">
        {user.isFuelManager() && (
          <div className="">
            <h2 className="font-semibold text-3xl">Settlements</h2>
            <span className="text-sm text-slate-400">
              View a list of all unsettled payments and perform actions.
            </span>
          </div>
        )}

        {user.isFuelAdmin() && (
          <div className="flex flex-col md:flex-row md:justify-between ">
            <div className="">
              <h2 className="font-semibold text-3xl">Settlements</h2>
              <span className="text-sm text-slate-400">
                View a list of all unsettled sales and their associated details.
              </span>
            </div>

            {/* Unclosed momo sales card */}
            {unsettledFuelBalance > 0 && (
              <div
                className={`bg-white w-full p-4 lg:p-4 rounded-[10px] shadow-lg md:w-1/2 lg:w-1/3 md:justify-end mt-5 md:mt-0`}>
                <div className="">
                  <h2 className="text-base font-semibold text-gray-700">Unsettled Payments</h2>

                  <div className="flex justify-between items-center">
                    <div className="flex  items-center gap-5">
                      <div className="bg-primary-main p-2 h-fit w-fit rounded-full ">
                        <DevicePhoneMobileIcon className="h-4 w-4 text-white" />
                      </div>

                      <div className="">
                        <div className="text-4xl font-bold text-gray-800">
                          GHS {unsettledFuelBalance}
                        </div>
                        <span className="text-gray-500">sales yet to be settled</span>
                      </div>
                    </div>
                    <div
                      onClick={adminRequestNonCashSettlement}
                      className="border border-[#1AAE9F] text-[#1AAE9F] rounded w-fit h-fit px-2 py-1  hover:bg-primary-border flex items-center gap-2 cursor-pointer">
                      <span className="font-medium text-sm"> Request Payment</span>
                      <div className="">
                        <IoWalletOutline className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {user.isFuelAdmin() && (
          <>
            <div className="overflow-x-auto mt-5">
              <TabNav
                classes="text-sm"
                tabs={getTabs()}
                activeTab={activeTab}
                setActiveTab={(id: string) => setActiveTab(id)}
              />
            </div>
            <TabContent id="PENDING" activeTab={activeTab}>
              <div className={''}>
                <GTable
                  columns={columns}
                  data={completedSales ?? []}
                  showSearch={false}
                  tableType="sale"
                  columnClasses=""
                  classes="h-fit"
                  loading={loading}
                  showPerPage={showPerPage}
                  onShowPerPageChange={(page: number) => {
                    setShowPerPage(page);
                    setToggleSaleDetailsView(true);
                  }}
                  onRowClick={(sale: any) => {
                    setSelectedSale(sale);
                    setToggleSaleDetailsView(true);
                  }}
                />
              </div>
            </TabContent>
            <TabContent id="CLOSED" activeTab={activeTab}>
              <div className={''}>
                <GTable
                  columns={columns}
                  data={completedSales ?? []}
                  showSearch={false}
                  tableType="sale"
                  columnClasses=""
                  classes="h-fit"
                  loading={loading}
                  showPerPage={showPerPage}
                  onShowPerPageChange={(page: number) => {
                    setShowPerPage(page);
                    setToggleSaleDetailsView(true);
                  }}
                  onRowClick={(sale: any) => {
                    setSelectedSale(sale);
                    setToggleSaleDetailsView(true);
                  }}
                />
              </div>
            </TabContent>
          </>
        )}

        {user.isFuelManager() && (
          <>
            <div className="flex items-center gap-5 mt-5">
              <TabNav
                tabs={getSettlemtTabs()}
                activeTab={activeSettlementTab}
                setActiveTab={(id: string) => {
                  setActiveSettlementTab(id);
                }}
              />
            </div>

            <TabContent id={'unsettled'} activeTab={activeSettlementTab}>
              <GTable
                columns={paymentSettlementColumns}
                showCheckboxes
                data={nonCashSettlements ?? []}
                showSearch={false}
                tableType="unsettled payment"
                columnClasses=""
                classes="h-fit"
                loading={isSettlementLoading}
                showPerPage={showPerPage}
                onShowPerPageChange={(page: number) => {
                  setShowPerPage(page);
                  setToggleSaleDetailsView(true);
                }}
              />
            </TabContent>

            <TabContent id={'settled'} activeTab={activeSettlementTab}>
              <div className={''}>
                <GTable
                  columns={paymentSettlementColumns}
                  showCheckboxes
                  data={nonCashSettlements ?? []}
                  showSearch={false}
                  tableType="settled payment"
                  columnClasses=""
                  classes="h-fit"
                  loading={isSettlementLoading}
                  showPerPage={showPerPage}
                  onShowPerPageChange={(page: number) => {
                    setShowPerPage(page);
                    setToggleSaleDetailsView(true);
                  }}
                />
              </div>
            </TabContent>
          </>
        )}
      </div>

      {toggleSaleDetailsView && selectedSale && (
        <UnclosedSaleDetails
          selectedSale={selectedSale}
          onClose={() => setToggleSaleDetailsView(false)}
          onProceed={() => queryClient.invalidateQueries({ queryKey: ['fetchUnclosedSales'] })}
          show={toggleSaleDetailsView}
          columns={transactionsColumns}
        />
      )}
    </Page>
  );
};

export default FuelSalesAuditview;
